import { useMemo } from 'react';
import { DatabaseEvent } from '@/types/event';
import { useDatabaseEventsStore } from '@/stores';
import { SortOption } from '@/utils/query-filters';
import {
    PaginatedEventsResult,
    mergeLiveDatabaseEvents,
    useSelectedSourceIds,
} from './eventsDataShared';
import {
    UseDatabaseEventsQueryOptions,
    useDatabaseEventsQuery,
} from './useDatabaseEventsQuery';
import { usePaginatedQueryResult } from './usePaginatedQueryResult';

export type UsePaginatedDatabaseEventsOptions = UseDatabaseEventsQueryOptions;

export function usePaginatedDatabaseEvents(
    options: UsePaginatedDatabaseEventsOptions = {},
): PaginatedEventsResult<DatabaseEvent> {
    const query = useDatabaseEventsQuery(options);
    const result = usePaginatedQueryResult<DatabaseEvent>(query);

    const liveEvents = useDatabaseEventsStore(
        (state) => state.recentDatabaseEvents,
    );
    const selectedSourceIds = useSelectedSourceIds(options.sourceId);
    const isNewestFirst =
        (options.sortOption ?? SortOption.DATE_NEWEST) ===
        SortOption.DATE_NEWEST;

    const events = useMemo(() => {
        if (result.page !== 1 || !isNewestFirst) return result.events;
        return mergeLiveDatabaseEvents(result.events, liveEvents, {
            sourceId: selectedSourceIds,
            startTime: query.filterParams.startTime,
            endTime: query.filterParams.endTime,
        });
    }, [
        result.page,
        result.events,
        isNewestFirst,
        liveEvents,
        selectedSourceIds,
        query.filterParams,
    ]);

    return { ...result, events };
}
